/**
 * Hook to generate BreakdownCell[] data for BreakdownTable component
 * Uses new simplified transform pipeline
 */

import { useMemo } from 'react';
import { transformForecastToBreakdownCells } from '@/utils/breakdown-transforms/forecastToBreakdownCells';
import { addCustomColumns } from '@/utils/breakdown-transforms/customColumns';
import {
  getCurrentYearPointsByPrimaryLine,
  getPreviousYearPoints,
} from '@/utils/rebateForecastingUtils';
import {
  DimensionType,
  CustomColumn,
  MultiYearForecastData,
  TimeFilterPreset,
  DimensionGroupingConfig,
  LineType,
} from '@/types/rebateAnalytics';
import type { BreakdownCell } from '@/components/ui/breakdown-tables/types';

interface UseBreakdownCellsProps {
  multiYearForecastData: MultiYearForecastData;
  // 4-dimension structure
  primaryXDimension?: DimensionType;
  secondaryXDimension?: DimensionType;
  primaryYDimension?: DimensionType;
  secondaryYDimension?: DimensionType;
  customColumns?: CustomColumn[];
  timeFilterPreset?: TimeFilterPreset;
  // Grouping configuration
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >;
  // If true, fill all sub-dimension combinations with zero values (default: false)
  fillMissingSubdimensions?: boolean;
  // Primary line selection (determines which data source to use for current year)
  primaryLine?: LineType;
}

/**
 * Hook to generate BreakdownCell[] data ready for BreakdownTable component
 * Uses new simplified transform pipeline
 */
export function useBreakdownCells({
  multiYearForecastData,
  primaryXDimension,
  secondaryXDimension,
  primaryYDimension,
  secondaryYDimension,
  customColumns,
  timeFilterPreset,
  dimensionGroupingConfig,
  fillMissingSubdimensions = false,
  primaryLine = 'sarima', // Default to SARIMA prediction algorithm
}: UseBreakdownCellsProps): BreakdownCell[] {
  return useMemo(() => {
    const currentYearData = multiYearForecastData.currentYearData;
    const previousYearData = multiYearForecastData.previousYearData;

    if (!currentYearData) {
      return [];
    }

    // Extract points based on primary line selection
    // Current year uses selected primary line, previous year always uses Estimates
    const currentYearPoints = getCurrentYearPointsByPrimaryLine(
      currentYearData,
      primaryLine,
    );
    const previousYearPoints = getPreviousYearPoints(previousYearData);

    // Convert grouping configs to Map format
    const groupingConfigsMap = dimensionGroupingConfig
      ? new Map(
          Object.entries(dimensionGroupingConfig) as Array<
            [DimensionType, DimensionGroupingConfig]
          >,
        )
      : undefined;

    // Main transformation pipeline
    let cells = transformForecastToBreakdownCells({
      currentYearPoints,
      previousYearPoints,
      currentYear: multiYearForecastData.currentYear,
      previousYear: multiYearForecastData.currentYear - 1,
      rules: currentYearData.rules,
      agreements: currentYearData.agreements,
      suppliers: currentYearData.suppliers,
      dimensions: {
        primaryX: primaryXDimension ?? DimensionType.TIME,
        secondaryX: secondaryXDimension,
        primaryY: primaryYDimension ?? DimensionType.SUPPLIER,
        secondaryY: secondaryYDimension,
      },
      timeFilterPreset,
      groupingConfigs: groupingConfigsMap,
      fillMissingSubdimensions,
      isCumulative: multiYearForecastData.isCumulative ?? false,
    });

    // Add custom columns if any
    if (customColumns && customColumns.length > 0) {
      cells = addCustomColumns({
        cells,
        customColumns,
        secondaryXDimension,
      });
    }

    return cells;
  }, [
    multiYearForecastData,
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
    customColumns,
    timeFilterPreset,
    dimensionGroupingConfig,
    fillMissingSubdimensions,
    primaryLine,
  ]);
}
